import React, { useEffect, useState } from 'react';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningIcon from '@mui/icons-material/Warning';
import axiosInstance from '../utlis/axiosInstance';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import styles from './PointsHistory.module.css';

interface PointsHistoryItem {
  id: number;
  created_at: string;
  description: string;
  points_remaining: number;
  points_used: number;
  usage_type: string;
  metadata: {
    user_id?: number;
    user_name?: string;
    [key: string]: any;
  };
}

const usageTypeIcon = (usageType: string) => {
  switch (usageType) {
    case 'USER_CREATION':
      return <PersonAddAltIcon sx={{ fontSize: 24, color: '#059669' }} />;
    case 'VEHICLE_TRACKING':
      return <DirectionsCarIcon sx={{ fontSize: 24, color: '#2563eb' }} />;
    case 'GEOFENCING':
      return <LocationOnIcon sx={{ fontSize: 24, color: '#ea580c' }} />;
    case 'REPORTS':
      return <DescriptionIcon sx={{ fontSize: 24, color: '#7c3aed' }} />;
    case 'NOTIFICATIONS':
      return <NotificationsIcon sx={{ fontSize: 24, color: '#dc2626' }} />;
    default:
      return <CreditCardIcon sx={{ fontSize: 24, color: '#1e40af' }} />;
  }
};

const getUsageTypeClass = (usageType: string) => {
  switch (usageType) {
    case 'USER_CREATION':
      return styles.userCreation;
    case 'VEHICLE_TRACKING':
      return styles.vehicleTracking;
    case 'GEOFENCING':
      return styles.geofencing;
    case 'REPORTS':
      return styles.reports;
    case 'NOTIFICATIONS':
      return styles.notifications;
    default:
      return styles.default;
  }
};

const formatUsageType = (usageType: string) =>
  usageType
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());

const PointsHistory: React.FC = () => {
  const navigate = useNavigate();
  const [pointsHistory, setPointsHistory] = useState<PointsHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userPoints, setUserPoints] = useState<number>(0);

  // Get user from localStorage (as in other web pages)
  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  })();

  useEffect(() => {
    fetchPointsHistory();
    if (user?.id) {
      getUserPoints();
    }
    // eslint-disable-next-line
  }, [user?.id]);

  const fetchPointsHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/gpstracker/pointshandler/');
      if (response?.data?.responseData) {
        const historyData = response.data.responseData.recent_history || [];
        setPointsHistory([...historyData].reverse());
      } else {
        setPointsHistory([]);
      }
    } catch (err: any) {
      setError('Failed to load points history.');
      setPointsHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const getUserPoints = async () => {
    try {
      const response = await axiosInstance.get(`/gpstracker/userhandler/${user.id}`);
      if (response.data?.status && response.data) {
        let responseData;
        if (typeof response.data === 'string') {
          try {
            const sanitizedData = response.data.replace(/"points":\s*Infinity/g, '"points": 999999999');
            responseData = JSON.parse(sanitizedData);
          } catch (parseError) {
            setUserPoints(0);
            return;
          }
        } else {
          responseData = response.data;
        }
        const points = responseData.responseData?.points;
        if (typeof points === 'number' && isFinite(points)) {
          setUserPoints(points);
        } else {
          setUserPoints(0);
        }
      }
    } catch (error) {
      setUserPoints(0);
    }
  };

  // Calculate summary statistics
  const totalPointsUsed = pointsHistory.reduce((sum, item) => sum + item.points_used, 0);
  const totalTransactions = pointsHistory.length;
  const recentActivity = pointsHistory.slice(0, 5).length;

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        {/* Header */}
        <div className={styles.header}>
          <button onClick={() => navigate(-1)} className={styles.backButton}>
            <ArrowBackIcon sx={{ fontSize: 24 }} />
          </button>
          <h1 className={styles.headerTitle}>Points History</h1>
        </div>

        {/* Stats Grid */}
        <div className={styles.statsGrid}>
          {/* Current Points Card */}
          <div className={styles.pointsCard}>
            <div className={styles.pointsAvatar}>
              <EmojiEventsIcon sx={{ fontSize: 32, color: 'white' }} />
            </div>
            <div className={styles.pointsInfo}>
              <p className={styles.pointsLabel}>Current Balance</p>
              <h2 className={styles.pointsValue}>
                {userPoints >= 0 ? userPoints.toLocaleString() : 0} Points
              </h2>
            </div>
          </div>

          {/* Total Points Used Card */}
          <div className={styles.pointsCard}>
            <div className={styles.pointsAvatar}>
              <RemoveCircleIcon sx={{ fontSize: 32, color: 'white' }} />
            </div>
            <div className={styles.pointsInfo}>
              <p className={styles.pointsLabel}>Total Points Used</p>
              <h2 className={styles.pointsValue}>
                {totalPointsUsed.toLocaleString()} Points
              </h2>
            </div>
          </div>

          {/* Total Transactions Card */}
          <div className={styles.pointsCard}>
            <div className={styles.pointsAvatar}>
              <AddCircleIcon sx={{ fontSize: 32, color: 'white' }} />
            </div>
            <div className={styles.pointsInfo}>
              <p className={styles.pointsLabel}>Total Transactions</p>
              <h2 className={styles.pointsValue}>
                {totalTransactions} Transactions
              </h2>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className={styles.loadingSpinner}>
            <div className={styles.loadingText}>Loading points history...</div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className={styles.alert}>
            <WarningIcon sx={{ fontSize: 20 }} />
            {error}
          </div>
        )}

        {/* Points History Section */}
        {!loading && !error && (
          <div className={styles.historySection}>
            <div className={styles.historyHeader}>
              <h2 className={styles.historyTitle}>Transaction History</h2>
              <p className={styles.historySubtitle}>
                Recent activity and point usage details
              </p>
            </div>

            {pointsHistory.length === 0 ? (
              <div className={styles.emptyState}>
                <p className={styles.emptyText}>No points history found.</p>
              </div>
            ) : (
              <ul className={styles.historyList}>
                {pointsHistory.map((item) => (
                  <li key={item.id} className={styles.historyItem}>
                    <div className={`${styles.itemAvatar} ${getUsageTypeClass(item.usage_type)}`}>
                      {usageTypeIcon(item.usage_type)}
                    </div>
                    <div className={styles.itemContent}>
                      <div className={styles.itemHeader}>
                        <h3 className={styles.itemTitle}>
                          {formatUsageType(item.usage_type)}
                        </h3>
                        {item.metadata?.user_name && (
                          <span className={styles.itemUser}>
                            User: {item.metadata.user_name}
                          </span>
                        )}
                      </div>
                      <p className={styles.itemDescription}>
                        {item.description}
                      </p>
                      <div className={styles.itemStats}>
                        <span className={`${styles.statItem} ${styles.pointsUsed}`}>
                          Points Used: {item.points_used}
                        </span>
                        <span className={`${styles.statItem} ${styles.pointsRemaining}`}>
                          Points Remaining: {item.points_remaining}
                        </span>
                      </div>
                      <p className={styles.itemDate}>
                        {moment(item.created_at).format('DD-MM-YYYY HH:mm:ss')}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PointsHistory; 